'use client';

import { ComponentPropsWithoutRef, forwardRef, type FC } from 'react';
import { ThreadPrimitive, useThread } from '@assistant-ui/react';
import { useThreadConfig, SuggestionConfig } from '@assistant-ui/react-ui';
import { withDefaults } from './utils/with-defaults';
import { ThreadWelcome as ThreadWelcomePrimitive } from '@assistant-ui/react-ui';
import Composer from './composer';
import { ArrowRightIcon } from 'lucide-react';
import Integrations from './integrations';

const ThreadWelcome: FC = () => {
    return (
        <ThreadWelcomeRoot>
            <ThreadWelcomeCenter className="">
                <div className="absolute top-0 left-0 flex flex-col gap-4 w-full">
                    <Integrations />
                </div>
                <div className="flex flex-col gap-8 items-center w-full">
                    <ThreadWelcomePrimitive.Avatar />
                    <ThreadWelcomeMessage />
                    <Composer />
                    <ThreadWelcomeSuggestions />
                </div>
            </ThreadWelcomeCenter>
        </ThreadWelcomeRoot>
    );
};

ThreadWelcome.displayName = 'ThreadWelcome';

const ThreadWelcomeRootStyled = withDefaults('div', {
    className: 'aui-thread-welcome-root',
});

const ThreadWelcomeCenter = withDefaults('div', {
    className: 'aui-thread-welcome-center gap-8',
});

namespace ThreadWelcomeRoot {
    export type Element = HTMLDivElement;
    export type Props = ComponentPropsWithoutRef<'div'>;
}

const ThreadWelcomeRoot = forwardRef<ThreadWelcomeRoot.Element, ThreadWelcomeRoot.Props>((props, ref) => {
    return (
        <ThreadPrimitive.Empty>
            <ThreadWelcomeRootStyled {...props} ref={ref} />
        </ThreadPrimitive.Empty>
    );
});

ThreadWelcomeRoot.displayName = 'ThreadWelcomeRoot';

const ThreadWelcomeMessageStyled = withDefaults('p', {
    className: 'aui-thread-welcome-message flex flex-col gap-1 font-medium text-lg items-center text-center',
});

namespace ThreadWelcomeMessage {
    export type Element = HTMLParagraphElement;
    export type Props = Omit<ComponentPropsWithoutRef<typeof ThreadWelcomeMessageStyled>, 'children'> & {
        message?: string | undefined;
    };
}

const ThreadWelcomeMessage = forwardRef<ThreadWelcomeMessage.Element, ThreadWelcomeMessage.Props>(
    ({ message: messageProp, ...rest }, ref) => {
        const { welcome: { message } = {}, strings: { welcome: { message: defaultMessage = '' } = {} } = {} } =
            useThreadConfig();
        return (
            <ThreadWelcomeMessageStyled {...rest} ref={ref}>
                {messageProp ?? message ?? defaultMessage}
                <div className="text-sm font-normal text-text-quaternary">How can I help you today?</div>
            </ThreadWelcomeMessageStyled>
        );
    }
);

ThreadWelcomeMessage.displayName = 'ThreadWelcomeMessage';

const ThreadWelcomeSuggestionContainer = withDefaults('div', {
    className: 'aui-thread-welcome-suggestions flex flex-col gap-3 w-full',
});

const ThreadWelcomeSuggestionStyled = withDefaults(ThreadPrimitive.Suggestion, {
    className: 'transition-colors duration-1000',
});

export namespace ThreadWelcomeSuggestion {
    export type Props = {
        suggestion: SuggestionConfig;
        className?: string;
    };
}

const ThreadWelcomeSuggestion: FC<ThreadWelcomeSuggestion.Props> = ({ suggestion: { text, prompt }, className }) => {
    return (
        <ThreadWelcomeSuggestionStyled prompt={prompt} method="replace" autoSend>
            {text ?? prompt}
        </ThreadWelcomeSuggestionStyled>
    );
};

const ThreadWelcomeSuggestions: FC = () => {
    const suggestions2 = useThread((t) => t.suggestions);
    const { welcome: { suggestions } = {} } = useThreadConfig();

    const finalSuggestions = suggestions2.length ? suggestions2 : suggestions;

    return (
        <ThreadWelcomeSuggestionContainer>
            {finalSuggestions?.map((suggestion, idx) => {
                const key = `${suggestion.prompt}-${idx}`;
                return <ThreadWelcomeSuggestion key={key} suggestion={suggestion} />;
            })}
        </ThreadWelcomeSuggestionContainer>
    );
};

ThreadWelcomeSuggestions.displayName = 'ThreadWelcomeSuggestions';

const exports = {
    Root: ThreadWelcomeRoot,
    Center: ThreadWelcomeCenter,
    // Avatar: ThreadWelcomeAvatar,
    Message: ThreadWelcomeMessage,
    Suggestions: ThreadWelcomeSuggestions,
    Suggestion: ThreadWelcomeSuggestion,
};

export default Object.assign(ThreadWelcome, exports) as typeof ThreadWelcome & typeof exports;
